import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import ClientsCard from "../../../clientTypes/components/ClientsCard";
import ProfileDialog from "../../../../components/ProfileDialog/ProfileDialog";
import { useIds } from "../../../../hook/AdminContext";
import { get_company_listing } from "../../../../DAL/features/features";
import { enqueueSnackbar } from "notistack";
import CircularLoader from "../../../../components/loader/CircularLoader";
import RecordNotFound from "../../../../components/recordNotFound/RecordNotFound";
import CompanyPopup from "../../../../components/popUp/CompanyPopup";
import GeneralMUIDialog from "../../../../components/GeneralMUIDialog/GeneralMUIDialog";
import FilterCompanies from "./components/FilterCompanies";
import AppliedFilters from "../../../../components/AppliedFilters/AppliedFilters";
import { CircularProgress } from "@mui/material";

export default function CompaniesPage({
  searchText = "",
  filterModal,
  setFilterModal,
  companyType = "",
  contentData = null,
  handleEditProfile,
}) {
  const { userInfo, handleGetSectorAccessValue } = useIds();
  const { source_slug, type_slug, feature_slug } = useParams();
  const [companiesList, setCompaniesList] = useState([]);
  const [filterData, setFilterData] = useState({
    type: null,
  });
  const [appliedFilterData, setAppliedFilterData] = useState(null);
  const [filterChange, setFilterChange] = useState(false);
  const [loading, setLoading] = useState(true);

  const [openDetails, setOpenDetails] = useState(false);
  const [targetCompany, setTargetCompany] = useState(null);

  const [companyStatus, setCompanyStatus] = useState(
    userInfo?.[companyType]?.publish || false
  );

  // load more functionality
  const [pageNumber, setPageNumber] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const observer = useRef();

  const lastBookElementRef = useCallback(
    (node) => {
      if (isLoadingMore) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && totalPages > pageNumber) {
          handleLoadMore();
        }
      });
      if (node) observer.current.observe(node);
    },
    [isLoadingMore, totalPages, pageNumber, observer]
  );

  const handleLoadMore = () => {
    setIsLoadingMore(true);
    setPageNumber(pageNumber + 1);
    handleGetCompaniesListing(pageNumber + 1, true);
  };

  const handleGetCompaniesListing = async (page = 0, loadingMore = false) => {
    if (!loadingMore) {
      setLoading(true);
    }
    const resp = await get_company_listing(
      source_slug,
      feature_slug,
      searchText,
      filterData?.type?.slug || "",
      page
    );
    if (resp.code == 200) {
      if (loadingMore) {
        setCompaniesList([...companiesList, ...resp?.data?.companies_list]);
      } else {
        if (
          (userInfo.role == "client" || userInfo.isCompanyProfile) &&
          handleGetSectorAccessValue(contentData?.companyTab.check_name) &&
          userInfo?.[companyType]?.publish
        ) {
          setCompaniesList([
            resp?.data?.my_company,
            ...resp?.data?.companies_list,
          ]);
        } else {
          setCompaniesList([...resp?.data?.companies_list]);
        }
      }
      setPageNumber(page);
      setTotalPages(resp.data.total_pages);
      setAppliedFilterData(filterData);
    } else {
      enqueueSnackbar(resp.message, { variant: "error" });
    }
    setIsLoadingMore(false);
    setLoading(false);
  };

  const handleFilterData = () => {
    setFilterModal(false);
    handleGetCompaniesListing();
  };

  useEffect(() => {
    if (companyType) {
      setCompanyStatus(userInfo?.[companyType]?.publish || false);
      handleGetCompaniesListing();
    }
  }, [userInfo?.[companyType]?.publish, searchText, filterChange]);

  return (
    <>
      {openDetails && (
        <CompanyPopup
          companyModalOpen={openDetails}
          handleCloseCompanyModal={() => setOpenDetails(false)}
          companyModalData={targetCompany}
          isCompanyProfile={true}
          companyType={companyType}
        />
      )}
      {filterModal && (
        <GeneralMUIDialog
          open={filterModal}
          setOpen={setFilterModal}
          actionText="Filter"
          title="Filter"
          content={
            <FilterCompanies inputs={filterData} setInputs={setFilterData} />
          }
          onSubmit={handleFilterData}
        />
      )}
      <div className="overview-coaches my-3">
        <div className="d-flex justify-content-between align-items-end tw-mb-3">
          <h6 className="mb-0">{contentData?.companyTab.heading}</h6>
        </div>

        {handleGetSectorAccessValue(contentData?.companyTab.check_name) &&
          !companyStatus &&
          ((userInfo.role == "client" && !userInfo?.isCompanyProfile) ||
            (userInfo?.isCompanyProfile &&
              userInfo?.company_access?.manage_profile?.company_profile)) && (
            <div className="company-card tw-p-4 company-profile-card mb-3">
              <div className="d-flex justify-content-between align-items-center gap-4">
                <div>
                  <div className="d-flex align-items-center mb-2">
                    <p className="mb-0 size-sm font-medium">
                      {contentData?.companyTab.profile_tab_heading}
                    </p>
                    <span
                      className={`status-badge ms-2 ${companyStatus ? "published" : "draft"
                        }`}
                    >
                      {companyStatus ? "Published" : "Draft"}
                    </span>
                  </div>
                  <p className="mb-0 text-gray">
                    {companyStatus
                      ? contentData?.companyTab.profile_tab_sub_heading_complete
                      : contentData?.companyTab
                        .profile_tab_sub_heading_incomplete}
                  </p>
                </div>

                <button
                  className="small-button radius-10"
                  onClick={() => {
                    handleEditProfile(true);
                  }}
                >
                  {companyStatus
                    ? contentData?.companyTab.edit_button_text
                    : contentData?.companyTab.draft_button_text}
                </button>
              </div>
            </div>
          )}
        {appliedFilterData && (
          <div>
            <AppliedFilters
              filterData={appliedFilterData}
              setFilterData={setFilterData}
              onFilterChange={() => {
                setFilterChange(!filterChange);
              }}
            />
          </div>
        )}

        {loading ? (
          <CircularLoader />
        ) : companiesList.length > 0 ? (
          <div className="conteiner">
            <div className="row">
              {companiesList.map((item, index) => (
                <>
                  <ClientsCard
                    item={item}
                    source_slug={source_slug}
                    type_slug={type_slug}
                    companyType={companyType}
                    isCompanyProfile={true}
                    handleNavigateDetails={(item) => {
                      setTargetCompany(item);
                      setOpenDetails(true);
                    }}
                    showDirectContactButton={true}
                    handleEditProfile={() =>
                      handleEditProfile(true, handleGetCompaniesListing)
                    }
                  />
                </>
              ))}
            </div>
          </div>
        ) : (
          <div>
            <>
              <RecordNotFound title="No Data" />
            </>
          </div>
        )}

        {!loading &&
          totalPages > 1 &&
          totalPages > pageNumber &&
          companiesList.length > 0 && (
            <div className="col-12 text-center mt-3">
              <div
                ref={lastBookElementRef}
                className="mt-3"
                onClick={() => {
                  handleLoadMore();
                }}
                id="load-more-feed"
              >
                {isLoadingMore ? (
                  <CircularProgress size={15} />
                ) : (
                  "Load More"
                )}
              </div>
            </div>
          )}
      </div>
    </>
  );
}
